import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Bell, X, Menu } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';

function timeAgo(date) {
  const s = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (s < 60) return 'agora';
  if (s < 3600) return `${Math.floor(s / 60)}min`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function Header({ onToggle }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const { data } = useQuery({
    queryKey: ['notificacoes-web'],
    queryFn: () => api.get('/notificacoes').then(r => r.data),
    refetchInterval: 15000,
  });

  const notifs = data?.data || [];
  const naoLidas = data?.nao_lidas || 0;

  useEffect(() => {
    if (naoLidas > 0 && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const prevCount = useRef(0);
  useEffect(() => {
    if (naoLidas > prevCount.current && naoLidas > 0 && Notification.permission === 'granted') {
      const latest = notifs.find(n => !n.lido);
      if (latest) new Notification(latest.titulo, { body: latest.mensagem, icon: '/logo-inbase.png' });
    }
    prevCount.current = naoLidas;
  }, [naoLidas]);

  // ── Push Notification Registration ────────────────────────────────────────
  useEffect(() => {
    // DEBUG TEMPORÁRIO — remover depois de diagnosticar a falha de inscrição no iOS PWA.
    // Manda cada etapa pro backend (logger.info) porque o console do Safari em iOS PWA
    // só é inspecionável plugando num Mac, e a sessão do Web Inspector cai quando o PWA fecha.
    const dbg = (stage, detail) => {
      api.post('/push/debug-log', { stage, detail: detail ? String(detail).slice(0, 300) : null }).catch(() => {});
    };

    dbg('effect-start', `sw=${'serviceWorker' in navigator} pushMgr=${'PushManager' in window} perm=${typeof Notification !== 'undefined' ? Notification.permission : 'sem-Notification-API'}`);

    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    // navigator.serviceWorker.ready espera o SW ficar ativo (evita corrida com o
    // registration feito em index.js no evento 'load', que roda depois do mount deste efeito)
    navigator.serviceWorker.ready.then((reg) => {
      dbg('sw-ready');
      // Verificar se já tem subscription ativa para evitar re-inscrição
      reg.pushManager.getSubscription().then((existingSub) => {
        if (existingSub) {
          dbg('already-subscribed', existingSub.endpoint);
          console.log('[Push] Já existe subscription ativa');
          return;
        }
        dbg('no-existing-sub-requesting-permission');

        // Solicitar permissão apenas se não tiver subscription
        Notification.requestPermission().then((permission) => {
          dbg('permission-result', permission);
          if (permission !== 'granted') {
            console.log('[Push] Permissão negada');
            return;
          }

          // Obter VAPID public key e subscribe
          api.get('/push/vapid-public-key').then((res) => {
            const { publicKey } = res.data;
            dbg('vapid-key-ok', publicKey ? publicKey.slice(0, 12) : 'vazia');

            reg.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: urlBase64ToUint8Array(publicKey),
            }).then((sub) => {
              dbg('subscribe-ok', sub.endpoint);
              api.post('/push/subscribe', { subscription: sub.toJSON() }).then(() => {
                dbg('save-ok');
                console.log('[Push] Inscrito com sucesso');
              }).catch((err) => {
                dbg('save-fail', err?.message || err);
                console.error('[Push] Erro ao salvar subscription:', err);
              });
            }).catch((err) => {
              dbg('subscribe-fail', err?.message || err);
              console.error('[Push] Erro ao subscribe:', err);
            });
          }).catch((err) => {
            dbg('vapid-key-fail', err?.message || err);
            console.error('[Push] Erro ao obter VAPID key:', err);
          });
        });
      });
    }).catch((err) => {
      dbg('sw-ready-fail', err?.message || err);
      console.error('[Push] Erro ao verificar registration:', err);
    });
  }, []);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const marcarLidas = async () => {
    await api.post('/notificacoes/marcar-lidas');
    qc.invalidateQueries(['notificacoes-web']);
  };

  function logout() {
    localStorage.removeItem('token');
    navigate('/login');
  }

  return (
    <header className="bg-white shadow-sm px-4 py-4 flex items-center justify-between flex-shrink-0">
      <button
        onClick={onToggle}
        title="Alternar menu"
        className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded"
      >
        <Menu size={22} />
      </button>
      <div className="flex items-center gap-4">
        <div className="relative" ref={ref}>
          <button onClick={() => setOpen(!open)} className="relative text-gray-500 hover:text-gray-700">
            <Bell size={20} />
            {naoLidas > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{naoLidas > 9 ? '9+' : naoLidas}</span>
            )}
          </button>
          {open && (
            <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-700">Notificações</span>
                {naoLidas > 0 && <button onClick={marcarLidas} className="text-xs text-blue-600 hover:underline">Marcar todas como lidas</button>}
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifs.length === 0 ? (
                  <p className="text-center text-gray-400 text-sm py-8">Nenhuma notificação</p>
                ) : notifs.slice(0, 20).map(n => (
                  <div key={n.id} className={`px-4 py-3 border-b border-gray-50 ${!n.lido ? 'bg-blue-50' : ''}`}>
                    <p className="text-sm font-medium text-gray-800">{n.titulo}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{n.mensagem}</p>
                    <p className="text-xs text-gray-400 mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <button onClick={logout} className="flex items-center gap-2 text-sm text-gray-600 hover:text-danger">
          <LogOut size={18} />
          Sair
        </button>
      </div>
    </header>
  );
}

// ── Helper: Converter VAPID key ───────────────────────────────────────────────
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

